import {makeTrackEvent} from './makeTrackEvent';

export type AgentChatLocation =
  | 'entity'
  | 'project'
  | 'run'
  | 'report'
  | 'global';

export type AgentChatAction =
  | 'navbar_button_viewed'
  | 'opened'
  | 'closed'
  | 'docked'
  | 'undocked'
  | 'chat_created'
  | 'chat_switched'
  | 'chat_renamed'
  | 'chat_deleted'
  | 'mention_added'
  | 'mention_removed'
  | 'suggestion_clicked'
  | 'message_sent'
  | 'turn_stopped'
  | 'step_expanded'
  | 'step_collapsed'
  | 'copied'
  | 'feedback_submitted';

export type AgentChatMentionVia =
  | 'composer_dropdown'
  | 'run_row_menu'
  | 'context_chip';
export type AgentChatMentionKind = 'run' | 'project';
export type AgentChatStepKind = 'tool_call' | 'reasoning';
export type AgentChatCopyTarget = 'message' | 'tool_call' | 'error';
export type AgentChatMessageVia = 'composer' | 'suggestion' | 'programmatic';

export type AgentChatInteractionPayload = {
  action: AgentChatAction;
  location: AgentChatLocation;
  entity_name?: string;
  project_name?: string;
  chat_id?: string;
  mention_via?: AgentChatMentionVia;
  mention_kind?: AgentChatMentionKind;
  suggestion_label?: string;
  step_kind?: AgentChatStepKind;
  tool_name?: string;
  copy_target?: AgentChatCopyTarget;
  feedback_is_positive?: boolean | null;
  feedback_has_reasoning?: boolean;
  message_via?: AgentChatMessageVia;
};

export const trackAgentChatInteraction = makeTrackEvent<
  AgentChatInteractionPayload,
  {
    action: {
      description: 'which agent-chat interaction the user performed';
    };
    location: {
      description: 'route context the user was in when the action fired';
    };
    entity_name: {
      description: 'entity (team or user) in the current route, when present';
      exampleValues: ['my-team', 'my-user'];
    };
    project_name: {
      description: 'project in the current route, when present';
      exampleValues: ['my-project'];
    };
    chat_id: {
      description: 'active agent chat id when known';
    };
    mention_via: {
      description: 'how the user added the mention';
    };
    mention_kind: {
      description: 'type of entity mentioned';
    };
    suggestion_label: {
      description: 'title of the suggestion card the user pressed';
      exampleValues: ['Summarize project', 'Recommend next experiment'];
    };
    step_kind: {
      description: 'which disclosure type was toggled';
    };
    tool_name: {
      description: 'tool name when step_kind is tool_call';
      exampleValues: ['shell', 'apply_patch'];
    };
    copy_target: {
      description: 'what type of content was copied to the clipboard';
    };
    feedback_is_positive: {
      description: 'thumbs up (true), thumbs down (false), or retraction (null)';
      exampleValues: [true, false, null];
    };
    feedback_has_reasoning: {
      description: 'whether the user supplied a reasoning string';
    };
    message_via: {
      description: 'how the message was submitted';
    };
    _description: 'User interactions with the W&B Agent UI';
    _location: 'W&B Agent chat button, panel, composer, history, message actions';
    _motivation: 'Measure adoption and per-surface engagement to guide UX investment';
  }
>('WB Agent Interaction');
